"use client";

import type React from "react";

import { useMemo, useRef, useState } from "react";
import { t } from "i18next"; // Import t from i18next
import { AnimatePresence, motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { useNavigate } from "react-router-dom";
import { useRealtimeKitMeeting, useRealtimeKitSelector } from "@cloudflare/realtimekit-react";

import { useClassroomStore } from "../store/useClassroomStore";

import LeaveCallDialog from "./LeaveCallDialog";
import { AudioParticipant, VideoParticipant } from "./MeetingParticipant.tsx";

import useUserStore from "@/features/auth/store/useUserStore.ts";
import { cn } from "@/lib/utils";
import ScreenShareView from "@/features/classroom/components/ScreenShareView.tsx";

// Transform Cloudflare participant to work with existing VideoParticipant component or handle screen sharing
function CloudflareVideoAdapter({ participant }: { participant: any }) {
  const self = useRealtimeKitSelector((m) => m.self);

  if (!participant) {
    return null;
  }

  const screenShareOn = participant?.screenShareEnabled ?? false;
  const screenShareStream =
    participant === self ? self.screenShareTracks.video : participant.screenShareTracks.video;

  if (screenShareOn && screenShareStream) {
    return <ScreenShareView compact={true} participant={participant} />;
  }

  return <VideoParticipant compact={true} participant={participant} />;
}

// Audio receiver component to handle all participant audio
function AudioReceiver() {
  const participants = useRealtimeKitSelector((m) => m.participants.joined.toArray());

  // Render audio for all participants (CloudflareAudioAdapter will filter out local)
  return (
    <>
      {participants.map((participant) => (
        <AudioParticipant key={participant.id} participant={participant} />
      ))}
    </>
  );
}

// Update PipMeeting to use a custom drag implementation
function PipMeeting() {
  const navigate = useNavigate();
  const { lessonLocation } = useClassroomStore();

  // Get store functions that won't cause re-renders
  const setIsPipMode = useClassroomStore((state) => state.setIsPipMode);
  const setShouldMaintainConnection = useClassroomStore(
    (state) => state.setShouldMaintainConnection,
  );
  const { user } = useUserStore();

  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);

  // Use direct coordinates for positioning
  const [position, setPosition] = useState(() => {
    // Default to bottom right
    return {
      x: window.innerWidth - 300,
      y: window.innerHeight - 200,
    };
  });

  const [isDragging, setIsDragging] = useState(false);
  const pipRef = useRef<HTMLDivElement>(null);

  // Reference to store the drag offset from the element's top-left corner
  const dragStartRef = useRef({
    offsetX: 0,
    offsetY: 0,
    startX: 0,
    startY: 0,
  });

  // Reference to store mouse move handler for cleanup
  const mouseMoveHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const mouseUpHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);

  // Get meeting context safely
  const { meeting } = useRealtimeKitMeeting();

  // Get participants and meeting state using Cloudflare selectors
  const participants = useRealtimeKitSelector((m) => Array.from(m.participants.joined.values()));
  const screensharingParticipants = useRealtimeKitSelector((m) =>
    m.participants.joined.toArray().find((p) => p.screenShareEnabled),
  );
  const self = useRealtimeKitSelector((m) => m.self);

  // Get native Cloudflare states
  const isAudioEnabled = useRealtimeKitSelector((m) => m.self?.audioEnabled ?? false);
  const isVideoEnabled = useRealtimeKitSelector((m) => m.self?.videoEnabled ?? false);

  // Get active speaker selector
  const activeSpeaker = useRealtimeKitSelector((meeting) => meeting.participants.lastActiveSpeaker);

  // Get the participant to display with proper priority
  const getParticipantToDisplay = () => {
    // Priority 1: Check for screen sharing participants (highest priority)
    // First check if local user is screen sharing
    if (self?.screenShareEnabled) {
      return self;
    }

    // Then check remote participants for screen sharing
    const screenSharingParticipant = participants.find((p) => p.screenShareEnabled);

    if (screenSharingParticipant) {
      return screenSharingParticipant;
    }

    // Priority 2: Last active speaker (excluding local participant)
    if (activeSpeaker && activeSpeaker !== self.id) {
      return participants.find((p) => p.id === activeSpeaker) || null;
    }

    // Priority 3: First non-local participant
    const firstNonLocalParticipant = participants[0];

    if (firstNonLocalParticipant) {
      return firstNonLocalParticipant;
    }

    // Priority 4: Local participant as fallback
    return self || null;
  };

  const participantToDisplay = useMemo(getParticipantToDisplay, [
    participants,
    self?.id,
    self?.screenShareEnabled,
    activeSpeaker,
    screensharingParticipants,
  ]);

  console.log(participantToDisplay);

  const handleReturnToCall = () => {
    // Mark that we're transitioning from PiP mode in the store
    const { setIsTransitioningFromPiP, setIsLoading } = useClassroomStore.getState();
    // const audioManager = getAudioManager(); // Removed AudioManager

    setIsTransitioningFromPiP(true);

    // Immediately set loading to false to prevent loading screen
    setIsLoading(false);

    // Mark AudioManager as transitioning to prevent audio issues
    // audioManager.setTransitioning(true); // Removed AudioManager

    // Mark audio elements as transitioning to help with speaker device management
    const audioElements = document.querySelectorAll("audio, video");

    audioElements.forEach((element) => {
      element.setAttribute("data-pip-transitioning", "true");
    });

    setIsPipMode(false);

    // Clean up transition markers and reset store state after a delay
    setTimeout(() => {
      audioElements.forEach((element) => {
        element.removeAttribute("data-pip-transitioning");
      });
      setIsTransitioningFromPiP(false);
      // audioManager.setTransitioning(false); // Removed AudioManager
    }, 3000); // Reduced timeout to allow speaker device changes sooner

    // Navigate to the stored lesson location
    if (lessonLocation) {
      navigate(lessonLocation);
      console.log(1);
    } else {
      console.log(2);
      // Extract the path from the URL using regex to find /workspaces/{workspaceId}/lessons/{lessonId}/call
      const path = window.location.pathname;
      const match = path.match(/(.+\/workspaces\/[^/]+\/lessons\/[^/]+)\/?.*/);

      if (match) {
        console.log(3);
        navigate(`${match[1]}/video`);
      }
    }
  };

  const handleLeaveClick = () => {
    setIsLeaveDialogOpen(true);
  };

  const handleLeaveConfirm = () => {
    setShouldMaintainConnection(false);

    // Use the globally stored leave function
    // @ts-ignore
    if (window.leaveMeeting) {
      // @ts-ignore
      window.leaveMeeting();
    }
    setIsLeaveDialogOpen(false);
    if (lessonLocation) {
      const match = lessonLocation.match(/\/workspaces\/([^/]+)\/lesson\/([^/]+)/);

      if (match) {
        const workspaceId = match[1];
        const lessonId = match[2];

        if (user && workspaceId && lessonId) {
          const detailsPath =
            user.role === "tutor"
              ? `/tutor/workspaces/${workspaceId}/lessons/${lessonId}/details`
              : `/student/workspaces/${workspaceId}/lessons/${lessonId}/details`;

          navigate(detailsPath);
        }
      }
    }
  };

  // Custom mouse handlers for precise dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    // Prevent default behavior to avoid text selection during drag
    e.preventDefault();

    // Get the initial position of the PiP
    if (!pipRef.current) return null;
    const rect = pipRef.current.getBoundingClientRect();

    // Calculate offset from the mouse position to the top-left corner of the PiP
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    // Store the starting position and offset
    dragStartRef.current = {
      offsetX,
      offsetY,
      startX: position.x,
      startY: position.y,
    };

    setIsDragging(true);

    // Setup mouse move handler
    const handleMouseMove = (e: MouseEvent) => {
      // Calculate new position based on mouse position minus the initial offset
      const newX = e.clientX - dragStartRef.current.offsetX;
      const newY = e.clientY - dragStartRef.current.offsetY;

      // Ensure the PiP stays within window boundaries
      const maxX = window.innerWidth - (pipRef.current?.offsetWidth || 280);
      const maxY = window.innerHeight - (pipRef.current?.offsetHeight || 180);

      setPosition({
        x: Math.max(0, Math.min(maxX, newX)),
        y: Math.max(0, Math.min(maxY, newY)),
      });
    };

    // Setup mouse up handler
    const handleMouseUp = () => {
      setIsDragging(false);

      // Remove event listeners
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);

      // Clear refs
      mouseMoveHandlerRef.current = null;
      mouseUpHandlerRef.current = null;
    };

    // Store handlers in refs for cleanup
    mouseMoveHandlerRef.current = handleMouseMove;
    mouseUpHandlerRef.current = handleMouseUp;

    // Add event listeners
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  // Handle touch events for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!pipRef.current || e.touches.length !== 1) return;

    // Prevent default behavior to avoid scrolling during drag
    e.preventDefault();

    const touch = e.touches[0];
    const rect = pipRef.current.getBoundingClientRect();

    // Calculate offset from the touch position to the top-left corner of the PiP
    const offsetX = touch.clientX - rect.left;
    const offsetY = touch.clientY - rect.top;

    // Store the starting position and offset
    dragStartRef.current = {
      offsetX,
      offsetY,
      startX: position.x,
      startY: position.y,
    };

    setIsDragging(true);

    // Setup touch move handler
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;

      const touch = e.touches[0];

      // Calculate new position based on touch position minus the initial offset
      const newX = touch.clientX - dragStartRef.current.offsetX;
      const newY = touch.clientY - dragStartRef.current.offsetY;

      // Ensure the PiP stays within window boundaries
      const maxX = window.innerWidth - (pipRef.current?.offsetWidth || 280);
      const maxY = window.innerHeight - (pipRef.current?.offsetHeight || 180);

      setPosition({
        x: Math.max(0, Math.min(maxX, newX)),
        y: Math.max(0, Math.min(maxY, newY)),
      });
    };

    // Setup touch end handler
    const handleTouchEnd = () => {
      setIsDragging(false);

      // Remove event listeners
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };

    // Add event listeners
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);
  };

  const toggleMicWithStore = async () => {
    // Get current store state for optimistic update
    const { micOn: storeMicOn, setMicOn } = useClassroomStore.getState();
    const newMicState = !storeMicOn;

    // Optimistic update - change UI immediately
    setMicOn(newMicState);

    try {
      // Don't change device settings during PiP mode - just toggle the mic state
      if (meeting) {
        if (newMicState) {
          await meeting.self.enableAudio();
        } else {
          await meeting.self.disableAudio();
        }
      }
    } catch (error) {
      console.error("Error toggling mic in PiP:", error);
      // Revert optimistic update on failure
      setMicOn(!newMicState);
    }
  };

  const toggleWebcamWithStore = async () => {
    // Get current store state for optimistic update
    const { webcamOn: storeWebcamOn, setWebcamOn } = useClassroomStore.getState();
    const newWebcamState = !storeWebcamOn;

    // Optimistic update - change UI immediately
    setWebcamOn(newWebcamState);

    try {
      // Don't change device settings during PiP mode - just toggle the webcam state
      if (meeting) {
        if (newWebcamState) {
          await meeting.self.enableVideo();
        } else {
          await meeting.self.disableVideo();
        }
      }
    } catch (error) {
      console.error("Error toggling webcam in PiP:", error);
      // Revert optimistic update on failure
      setWebcamOn(!newWebcamState);
    }
  };

  return (
    <>
      <AnimatePresence>
        <motion.div
          key="pip-container"
          animate={{ opacity: 1 }}
          className="pointer-events-none fixed inset-0 z-[1000]"
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
        >
          {/* Audio receiver for all participants */}
          <AudioReceiver />

          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            ref={pipRef}
            className={cn(
              "bg-background/95 border-border absolute overflow-hidden rounded-lg shadow-lg backdrop-blur-sm",
              "pointer-events-auto w-64",
              isDragging ? "cursor-grabbing" : "cursor-grab",
            )}
            style={{
              left: `${position.x}px`,
              top: `${position.y}px`,
              transition: isDragging ? "none" : "all 0.2s ease",
              touchAction: "none", // Prevents touch scrolling while dragging
              userSelect: "none", // Prevents text selection
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            {/* Video container */}
            <div className="aspect-video w-full">
              <CloudflareVideoAdapter participant={participantToDisplay} />
            </div>

            {/* Control buttons */}
            <div className="bg-background flex items-center justify-between p-1.5">
              <div className="flex gap-1">
                <button
                  className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-full transition-colors",
                    isAudioEnabled
                      ? "bg-muted hover:bg-muted/80"
                      : "bg-destructive hover:bg-destructive/90",
                  )}
                  title={isAudioEnabled ? t("classroom:muteMic") : t("classroom:unmuteMic")}
                  onClick={toggleMicWithStore}
                >
                  <Icon
                    className={cn(
                      "transition-colors",
                      isAudioEnabled ? "text-accent-foreground" : "text-destructive-foreground",
                    )}
                    height="14"
                    icon={isAudioEnabled ? "lucide:mic" : "lucide:mic-off"}
                    width="14"
                  />
                </button>

                <button
                  className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-full transition-colors",
                    isVideoEnabled
                      ? "bg-muted hover:bg-muted/80"
                      : "bg-destructive hover:bg-destructive/90",
                  )}
                  title={
                    isVideoEnabled ? t("classroom:disableCamera") : t("classroom:enableCamera")
                  }
                  onClick={toggleWebcamWithStore}
                >
                  <Icon
                    className={cn(
                      "transition-colors",
                      isVideoEnabled ? "text-accent-foreground" : "text-destructive-foreground",
                    )}
                    height="14"
                    icon={isVideoEnabled ? "lucide:video" : "lucide:video-off"}
                    width="14"
                  />
                </button>
              </div>

              <div className="flex gap-1">
                <button
                  className="bg-primary text-primary-foreground hover:bg-primary/90 flex h-9 w-9 items-center justify-center rounded-full transition-colors"
                  title={t("classroom:returnToCall")}
                  onClick={handleReturnToCall}
                >
                  <Icon height="14" icon="lucide:maximize-2" width="14" />
                </button>

                <button
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90 flex h-9 w-9 items-center justify-center rounded-full transition-colors"
                  title={t("classroom:leaveCall")}
                  onClick={handleLeaveClick}
                >
                  <Icon height="14" icon="lucide:phone-off" width="14" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Leave Call Dialog - moved outside AnimatePresence */}
      <LeaveCallDialog
        isOpen={isLeaveDialogOpen}
        onClose={() => setIsLeaveDialogOpen(false)}
        onConfirm={handleLeaveConfirm}
      />
    </>
  );
}

// @ts-ignore
export default function PictureInPicture() {
  // Subscribe only to isPipMode to minimize re-renders
  const isPipMode = useClassroomStore((state) => state.isPipMode);

  if (!isPipMode) {
    return null;
  }

  return <PipMeeting />;
}
